import {
	AdvancedMarker,
	APIProvider,
	Map as GoogleMap,
	// InfoWindow,
	// MapMouseEvent,
	Pin,
	useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { ShowOptions } from '@/app/[locale]/FormPostcode';

import type ZorgbaseResult from '@/lib/models/zorgbase/ZorgbaseResult';
import type {
	// haversineDistance,
	// positionToPoint,
	// sortByDistanceProperty,
	Position,
} from '@/lib/util';
import styles from './Map.module.scss';
import MapMarker from './MapMarker';
import OrganisationResult from './OrganisationResult';

function showInResults(item: ZorgbaseResult, accepts: ShowOptions) {
	let showResult = false;
	switch (accepts) {
		case ShowOptions.all:
			showResult = true;
			break;
		case ShowOptions.patients:
			showResult = Boolean(item.acceptsPatients);
			break;
		case ShowOptions.passersby:
			showResult = Boolean(item.acceptsPassersBy);
			break;
	}

	return showResult;
}

// export type MapDisplayType = "All" | "Patients" | "PassersBy";

export interface MapDisplayProps {
	organisations: ZorgbaseResult[];
	location: Position;
	type: ShowOptions;
	displayMap: boolean;
}

const INITIAL_RESULTS_TO_SHOW = 5;
const LOAD_MORE_INCREMENT = 5;

// https://react-google-maps-api-docs.netlify.app/
// biome-ignore lint/suspicious/noShadowRestrictedNames: function is named Map to match its role; renaming would require updating all import sites
function Map({ organisations: defaultOrganisations, location: defaultLocation, type, displayMap }: MapDisplayProps) {
	const t = useTranslations();
	const locale = useLocale();

	const [markerRef, _marker] = useAdvancedMarkerRef();
	const [location, setLocation] = useState(defaultLocation);
	const [numberOfResultsToShow, setNumberOfResultsToShow] = useState(INITIAL_RESULTS_TO_SHOW);

	// Sync location and reset results when props change
	const [prevDefaultLocation, setPrevDefaultLocation] = useState(defaultLocation);
	if (defaultLocation !== prevDefaultLocation) {
		setPrevDefaultLocation(defaultLocation);
		setLocation(defaultLocation);
	}

	const [prevDefaultOrganisations, setPrevDefaultOrganisations] = useState(defaultOrganisations);
	if (defaultOrganisations !== prevDefaultOrganisations) {
		setPrevDefaultOrganisations(defaultOrganisations);
		setNumberOfResultsToShow(INITIAL_RESULTS_TO_SHOW);
	}

	const filteredOrganisations = defaultOrganisations.filter((item) => showInResults(item, type));
	const organisationsToDisplay = filteredOrganisations.slice(0, numberOfResultsToShow);

	const hasMoreResults = filteredOrganisations.length > numberOfResultsToShow;

	const handleLoadMoreResults = () => {
		setNumberOfResultsToShow((prev) => prev + LOAD_MORE_INCREMENT);
	};

	return (
		<div className="flex-row" style={{ gap: '10px' }}>
			<div className="col-6">
				{organisationsToDisplay.map((item: ZorgbaseResult) => {
					return <OrganisationResult key={`or-${item.id}`} result={item} locale={locale} t={t} />;
				})}

				{hasMoreResults && (
					<div style={{ textAlign: 'center', marginTop: '20px' }}>
						<button type="button" onClick={handleLoadMoreResults}>
							{t('Generic.LoadMore')}
						</button>
					</div>
				)}
			</div>
			<div className={clsx('col-6', !displayMap ? styles.MapContainer : '')}>
				<APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY} libraries={['marker']} language={locale}>
					<GoogleMap
						style={{ height: '440px', width: '100%', overflow: 'hidden', borderRadius: '8px' }}
						clickableIcons={false}
						defaultZoom={13}
						// zoom={13}
						// controlled={true}
						reuseMaps={true}
						mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID}
						fullscreenControl={false}
						mapTypeControl={false}
						streetViewControl={false}
						zoomControl={true}
						tilt={0}
						disableDefaultUI={true}
						gestureHandling={'cooperative'}
						center={location}
					>
						{defaultOrganisations.map((item, index) => (
							<MapMarker organisation={item} type={type} key={`organisation-marker-${index}`} />
						))}

						<AdvancedMarker key="marker-me" ref={markerRef} position={location}>
							<Pin />
						</AdvancedMarker>
					</GoogleMap>
				</APIProvider>
			</div>
		</div>
	);
}

export default Map;
